/**
 * Karty / Seznam density for directory pickers (Obchody / Organizace / Oblíbené).
 *
 * Breakpoint: Tailwind `lg` (1024px) — same as CustomerAppShell desktop sidebar.
 * Compact (&lt;lg): always Seznam (`list`); density toggle hidden.
 * lg+: Karty (`cards`) default; optional sessionStorage preference per screen.
 */

import { useCallback, useEffect, useState } from 'react';

/** Seznam = list; Karty = cards. */
export type DirectoryDensityMode = 'list' | 'cards';

/** Tailwind `lg` — matches CustomerAppShell sidebar / content widen. */
export const DIRECTORY_LG_MQ = '(min-width: 1024px)';

/** Suggested sessionStorage keys per picker surface. */
export const DIRECTORY_DENSITY_STORAGE_KEYS = {
  orgs: 'directory-density:orgs',
  favorites: 'directory-density:favorites',
  shops: 'directory-density:shops',
} as const;

export type DirectoryDensityStorageKey =
  (typeof DIRECTORY_DENSITY_STORAGE_KEYS)[keyof typeof DIRECTORY_DENSITY_STORAGE_KEYS];

const DEFAULT_LG_DENSITY: DirectoryDensityMode = 'cards';

function isDensityMode(value: unknown): value is DirectoryDensityMode {
  return value === 'list' || value === 'cards';
}

export function readDirectoryDensityPreference(
  storageKey: string | undefined,
): DirectoryDensityMode | null {
  if (storageKey === undefined || storageKey.length === 0) {
    return null;
  }
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    return isDensityMode(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeDirectoryDensityPreference(
  storageKey: string | undefined,
  mode: DirectoryDensityMode,
): void {
  if (storageKey === undefined || storageKey.length === 0) {
    return;
  }
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(storageKey, mode);
  } catch {
    // Private mode / quota — preference stays in-memory only.
  }
}

function readLgUp(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // SSR / no matchMedia: treat as desktop so default Karty is available when hydrated.
    return true;
  }
  return window.matchMedia(DIRECTORY_LG_MQ).matches;
}

export interface UseDirectoryDensityResult {
  /** Effective density for rendering — always `list` below lg. */
  readonly density: DirectoryDensityMode;
  /** lg+ preference (may differ from `density` when compact). */
  readonly preference: DirectoryDensityMode;
  readonly setDensity: (mode: DirectoryDensityMode) => void;
  /** True at lg+ — pass to density toggle / find toolbar. */
  readonly showToggle: boolean;
  readonly isLgUp: boolean;
}

/**
 * Directory Karty/Seznam density.
 *
 * @param storageKey Optional sessionStorage key (e.g. `directory-density:shops`).
 *   When set, lg+ preference persists for the tab session.
 */
export function useDirectoryDensity(storageKey?: string): UseDirectoryDensityResult {
  const [isLgUp, setIsLgUp] = useState(readLgUp);
  const [preference, setPreference] = useState<DirectoryDensityMode>(() => {
    return readDirectoryDensityPreference(storageKey) ?? DEFAULT_LG_DENSITY;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia(DIRECTORY_LG_MQ);
    const onChange = (): void => {
      setIsLgUp(media.matches);
    };
    onChange();
    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);

  // storageKey rarely changes; rehydrate during render when it does (no effect setState).
  const [hydratedKey, setHydratedKey] = useState(storageKey);
  if (storageKey !== hydratedKey) {
    setHydratedKey(storageKey);
    setPreference(readDirectoryDensityPreference(storageKey) ?? DEFAULT_LG_DENSITY);
  }

  const setDensity = useCallback(
    (mode: DirectoryDensityMode) => {
      setPreference(mode);
      writeDirectoryDensityPreference(storageKey, mode);
    },
    [storageKey],
  );

  const density: DirectoryDensityMode = isLgUp ? preference : 'list';

  return {
    density,
    preference,
    setDensity,
    showToggle: isLgUp,
    isLgUp,
  };
}
